/**
 *
 * Tableau
 *
 */

import React, { FC, useState } from 'react';

import { AccountType } from '@ariksa/cloud-account';
import { useSelector } from 'react-redux';

import { Form, FormAction } from 'components/DataEntry/Form';
import { useIntegrations } from 'containers/Setup/Integrations/hooks/useIntegrations';
import { selectIntegrations } from 'containers/Setup/Integrations/selectors';
import { IntegrationsTabs } from 'containers/Setup/Integrations/types';

interface Props {
  action?: FormAction;
  accountDetails?: any;
  accountDetailsIsLoading?: boolean;
  onClickCancel?();
}

export const Tableau: FC<Props> = props => {
  const {
    action,
    accountDetails,
    accountDetailsIsLoading,
    onClickCancel,
  } = props;
  const { onCancel, updateAccount, onboardAccount } = useIntegrations();
  const { account } = useSelector(selectIntegrations);
  const [isEdit] = useState(action === 'Update');

  const handleReset = () => {
    onClickCancel?.();
    onCancel();
  };

  const handleSubmit = data => {
    const {
      name,
      host_and_port,
      auth_type,
      username,
      password,
      site_name,
      personal_access_token,
      personal_access_token_secret,
    } = data;

    const tableauPayload = {
      host_and_port,
      auth_type: auth_type?.value,
      username,
      password,
      site_name,
      personal_access_token,
      personal_access_token_secret,
    };

    if (isEdit) {
      updateAccount(
        {
          uuid: accountDetails?.uuid,
          cloudAccountUpdate: {
            name,
            cloud_type: IntegrationsTabs.Tableau,
            tableau: tableauPayload,
          },
        },
        AccountType.Saas,
      );
    } else {
      onboardAccount(
        {
          cloudAccountCreateRequest: {
            name,
            cloud_type: IntegrationsTabs.Tableau,
            tableau: tableauPayload,
          },
        },
        AccountType.Saas,
      );
    }
  };

  return (
    <Form
      schema={{
        name: {
          type: 'text',
          label: 'Name',
          placeholder: 'Enter name to be used by Ariksa',
          isRequired: true,
          defaultValue: isEdit ? accountDetails?.name : '',
          autoFocus: true,
        },
        host_and_port: {
          type: 'text',
          label: 'Host And Port',
          isRequired: true,
          defaultValue: isEdit ? accountDetails?.tableau?.host_and_port : '',
        },
        auth_type: {
          type: 'react-select',
          label: 'Authentication Type For Tableau',
          options: [
            { label: 'BasicAuth', value: 'BasicAuth' },
            { label: 'AccessTokenAuth', value: 'AccessTokenAuth' },
          ],
          defaultValue: isEdit
            ? {
                label: accountDetails?.tableau?.auth_type,
                value: accountDetails?.tableau?.auth_type,
              }
            : { label: 'BasicAuth', value: 'BasicAuth' },
        },
        username: {
          type: 'text',
          label: 'Username',
          isRequired: true,
          defaultValue: isEdit ? accountDetails?.tableau?.username : '',
          shouldDisplay: values => values.auth_type?.value === 'BasicAuth',
        },
        password: {
          type: 'text',
          label: (isEdit ? 'New ' : '') + 'Password',
          htmlInputType: 'password',
          isRequired: true,
          placeholder: 'Enter password',
          shouldDisplay: values => values.auth_type?.value === 'BasicAuth',
        },
        personal_access_token: {
          type: 'text',
          label: 'Personal Access Token',
          isRequired: true,
          defaultValue: isEdit
            ? accountDetails?.tableau?.personal_access_token
            : '',
          shouldDisplay: values =>
            values.auth_type?.value === 'AccessTokenAuth',
        },
        personal_access_token_secret: {
          type: 'text',
          label: 'Personal Access Token Secret',
          htmlInputType: 'password',
          isRequired: true,
          placeholder: 'Enter secret',
          shouldDisplay: values =>
            values.auth_type?.value === 'AccessTokenAuth',
        },
        site_name: {
          type: 'text',
          label: 'Site Name',
          defaultValue: isEdit ? accountDetails?.tableau?.site_name : '',
        },
      }}
      buttonOptions={{
        submit: {
          name: 'OK',
          isLoading: account.isLoading || accountDetailsIsLoading,
        },
        reset: {
          name: 'CANCEL',
          isVisible: true,
          onClick: handleReset,
        },
      }}
      handleSubmit={handleSubmit}
    />
  );
};

